import classNames                            from 'classnames';
import * as PropTypes                        from 'prop-types';
import * as React                            from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import DV                                    from 'ts-data-validator';

import { staticPageConstants } from '../../../../common/constants/staticPageConstants';

import RouterContent from './RouterContent';
import RouterNav     from './RouterNav';

const RouterTabs = function(props) {
  const history  = useHistory();
  const location = useLocation();

  const {
          content         = [],
          label,
          nav,
          renderStaticSection,
          style,
          tabsRouterLevel = 1,
          url,
        } = props;

  const [isOpen, setIsOpen] = React.useState(false);
  const [child, setChild]   = React.useState({});

  const params             = React.useMemo(() => {
    return location.pathname
      .split('/')
      .filter((item) => {
        return item !== '';
      });
  }, [location.pathname]);
  const prevParams         = React.useMemo(() => {
    return params.slice(0, tabsRouterLevel);
  }, [params, tabsRouterLevel]);
  const prevChildrenParams = React.useMemo(() => {
    return params.slice(0, tabsRouterLevel + 1);
  }, [params, tabsRouterLevel]);
  const tabIndex           = React.useMemo(() => {
    return url.findIndex((item) => {
      return item === params[prevParams.length];
    });
  }, [params, prevParams.length, url]);
  const currentTab = React.useMemo(() => {
    return content[tabIndex] ?? [];
  }, [content, tabIndex]);
  const hasChildren        = React.useMemo(() => {
    const [block = {}] = content[tabIndex] ?? [];

    return (
      style === staticPageConstants.COMPONENTS.TABS_WITH_ROUTER.SETTINGS.STYLE.SELECT &&
      block.DATA?.STYLE === staticPageConstants.COMPONENTS.TABS_WITH_ROUTER.SETTINGS.STYLE.SELECT &&
      block.COMPONENT === staticPageConstants.COMPONENTS.TABS_WITH_ROUTER.LABEL
    );
  }, [currentTab, style]);
  const childTab           = React.useMemo(() => {
    const [block = {}] = content[tabIndex] ?? [];

    return {
      label: block.DATA?.LABEL,
      nav:   block.DATA?.NAV,
      url:   block.DATA?.URL,
    };
  }, [content, tabIndex]);
  const childTabIndex      = React.useMemo(() => {
    return childTab.url?.findIndex((item) => {
      return item === params[prevChildrenParams.length];
    }) ?? -1;
  }, [childTab.url, params, prevChildrenParams]);

  const changeState    = React.useCallback((open) => {
    if (child[tabIndex] !== open) {
      setChild({
        ...child,
        [tabIndex]: open,
      });
    }
  }, [child, tabIndex]);
  const changeTab      = React.useCallback((index) => {
    const path = prevParams.join('/');

    history.replace(`/${path}/${url[index]}`, { scrollY: window.scrollY });
  }, [prevParams, tabsRouterLevel, url]);
  const changeChildTab = React.useCallback((index) => {
    const path = prevChildrenParams.join('/');

    history.replace(`/${path}/${childTab.url[index]}`, { scrollY: window.scrollY });
  }, [childTab, prevChildrenParams, tabIndex, tabsRouterLevel]);

  React.useEffect(() => {
    if (DV.isNumber(location.state?.scrollY)) {
      scrollTo({
        left: 0,
        top:  location.state.scrollY,
      });
    }
  }, [location.state?.scrollY]);

  if (tabIndex === -1) {
    return (
      <Redirect
        to={{
          pathname: `/${prevParams.join('/')}/${url[0]}`,
          state:    { scrollY: typeof window !== 'undefined' ? window.scrollY : void 0 },
        }}
      />
    );
  }

  if (hasChildren) {
    if (childTabIndex === -1) {
      return (
        <Redirect
          to={{
            pathname: `/${prevChildrenParams.join('/')}/${childTab.url[0]}`,
            state:    { scrollY: typeof window !== 'undefined' ? window.scrollY : void 0 },
          }}
        />
      );
    }

    return (
      <div className={classNames('tabbable static', style)}>
        <div className={'top'}>
          <RouterNav
            active={tabIndex}
            changeState={setIsOpen}
            changeTab={changeTab}
            label={label}
            nav={nav}
            open={isOpen}
            style={style}
          />
          <RouterNav
            active={childTabIndex}
            changeState={changeState}
            changeTab={changeChildTab}
            label={childTab.label}
            nav={childTab.nav}
            open={child[tabIndex] || false}
            style={style}
          />
        </div>
        <RouterContent active={tabIndex}>
          {content.map((tab, t) => (
            <div key={t}>
              {t === tabIndex
                ? (
                  <React.Fragment>
                    {(tab || [])[0].DATA.CONTENT[childTabIndex].map((block, b) => renderStaticSection({
                      ...block,
                      DATA: {
                        ...block.DATA,
                        TABS_ROUTER_LEVEL: tabsRouterLevel + 2,
                      },
                    }, b))}
                    {(tab || []).slice(1)
                      .map((block, b) => renderStaticSection({
                        ...block,
                        DATA: {
                          ...block.DATA,
                          TABS_ROUTER_LEVEL: tabsRouterLevel + 2,
                        },
                      }, b))}
                  </React.Fragment>
                )
                : (tab || []).map((block, b) => renderStaticSection({
                  ...block,
                  DATA: {
                    ...block.DATA,
                    TABS_ROUTER_LEVEL: tabsRouterLevel + 1,
                  },
                }, b))
              }
              {}
            </div>
          ))}
        </RouterContent>
      </div>
    );
  }

  return (
    <div className={classNames('tabbable static', style)}>
      <div className={'top'}>
        <RouterNav
          active={tabIndex}
          changeState={setIsOpen}
          changeTab={changeTab}
          label={label}
          nav={nav}
          open={isOpen}
          style={style}
        />
      </div>
      <RouterContent active={tabIndex}>
        {content.map((tab, t) => (
          <div key={t}>
            {(tab || []).map((block, b) => renderStaticSection({
              ...block,
              DATA: {
                ...block.DATA,
                TABS_ROUTER_LEVEL: tabsRouterLevel + 1,
              },
            }, b))}
          </div>
        ))}
      </RouterContent>
    </div>
  );
};

RouterTabs.propTypes = {
  active:          PropTypes.number,
  content:         PropTypes.array,
  label:           PropTypes.string,
  nav:             PropTypes.array,
  style:           PropTypes.string,
  tabsRouterLevel: PropTypes.number,
  url:             PropTypes.array,
};

export default RouterTabs;